import React, { useEffect, useRef } from 'react';
import {
    useFloating,
    offset,
    shift,
    arrow,
    autoUpdate,
    FloatingPortal,
    Placement,
    FloatingArrow,
    hide,
} from '@floating-ui/react';
import { css, Theme, useTheme } from '@emotion/react';

import { NOOP } from "core/constants";
import CloseButton from "core/Components/CloseButton";

export interface FloatingNotificationProps {
    referenceEl: HTMLElement;
    message: string;
    placement?: Placement;
    autoCloseMs?: number;
    onClose?: () => void;
    disableAutoDismiss?: boolean,
}

export const FloatingNotification: React.FC<FloatingNotificationProps> = ({
    referenceEl,
    message,
    placement = 'bottom',
    autoCloseMs = 3000,
    onClose = NOOP,
    disableAutoDismiss = false,
}) => {
    const theme = useTheme();
    const arrowRef = useRef<SVGSVGElement | null>(null);

    const {
        refs,
        floatingStyles,
        context,
        middlewareData,
    } = useFloating({
        placement,
        middleware: [offset(8), shift({ padding: 8 }), arrow({ element: arrowRef }), hide()],
        whileElementsMounted: autoUpdate,
    });

    React.useLayoutEffect(function setReferenceEl() {
        refs.setReference(referenceEl);
    }, [refs, referenceEl]);

    useEffect(function autoDismissToast() {
        if (!autoCloseMs || disableAutoDismiss) return;
        const id = setTimeout(() => onClose?.(), autoCloseMs);
        return () => clearTimeout(id);
    }, [message, autoCloseMs, onClose, disableAutoDismiss]);

    if (middlewareData.hide?.referenceHidden) return null;

    return (
        <FloatingPortal>
            <div
                ref={refs.setFloating}
                style={floatingStyles}
                css={style({ isHidden: !!middlewareData.hide?.referenceHidden })}
            >
                <FloatingArrow
                    ref={arrowRef}
                    context={context}
                    width={12}
                    height={6}
                    fill={theme.colours.apple}
                />
                {message}
                {onClose !== NOOP && (
                    <CloseButton onClick={onClose} />
                )}
            </div>
        </FloatingPortal>
    );
};

const style = ({ isHidden }: { isHidden: boolean; }) => (theme: Theme) => css`
    display: flex;
    justify-content: space-between;
    z-index: ${theme.zIndex.zIndexHighest};
    background: ${theme.colours.apple};
    color: white;
    border-radius: 4px;
    padding: 12px 40px;
    font-size: 14px;
    font-weight: ${theme.fonts.weights.normal};
    box-shadow: ${theme.borderAndShadow.boxShadow};

    opacity: 0;
    animation: fade-in 300ms ease forwards;
    
    @keyframes fade-in {
        to {
            opacity: 1;
        }
    }

    ${isHidden && css`
        visibility: hidden;
        pointer-events: none;
    `};
`;

export default FloatingNotification;
